import Utils from '../Utils.jsx';
import { serializeTaskForAi } from './serializeTask.js';
import {
	SCHEDULE_SYSTEM,
	REPOSITORY_SYSTEM,
	NARRATIVE_SYSTEM,
	scheduleUserPrompt,
	repositoryUserPrompt,
	narrativeUserPrompt
} from './prompts.js';

async function complete(dispatch, systemPrompt, userPrompt, options = {}) {
	const endpoint = options.endpoint || 'adminAiComplete';
	const payload = {
		systemPrompt,
		userPrompt
	};
	if (options.provider) payload.provider = options.provider;
	const result = await Utils.ajaxAsync(endpoint, payload, dispatch, { suppressErrorAlert: true });
	if (!result.success) {
		throw new Error(result.error || 'AI request failed');
	}
	return result.text;
}

const NARRATIVE_WAIT_MS = 12 * 60 * 1000;

export async function generateScheduleNarrative (payload, dispatch, onProgress, endpoints) {
	const startEndpoint = (endpoints && endpoints.start) || 'aiNarrative';
	const statusEndpoint = (endpoints && endpoints.status) || 'aiNarrativeStatus';
	const started = await Utils.ajaxAsync(startEndpoint, payload, dispatch, { suppressErrorAlert: true });
	if (!started || typeof started !== 'object') {
		throw new Error('Narrative request failed');
	}
	if (onProgress) onProgress(started);
	if (started.dryRun || started.status === 'done' || started.status === 'error' || !started.jobId) {
		return started;
	}
	const deadline = Date.now() + NARRATIVE_WAIT_MS;
	let latest = started;
	while (Date.now() < deadline) {
		await new Promise(resolve => setTimeout(resolve, 2000));
		latest = await Utils.ajaxAsync(statusEndpoint, { jobId: started.jobId }, null, { suppressErrorAlert: true });
		if (!latest || typeof latest !== 'object') {
			throw new Error('Narrative status failed');
		}
		if (onProgress) onProgress(latest);
		if (latest.status === 'done' || latest.status === 'error') {
			return latest;
		}
		if (latest.status === 'pending' && latest.hint) {
			return {
				success: false,
				status: latest.status,
				jobId: started.jobId,
				error: latest.hint,
				debug: latest.debug || null
			};
		}
	}
	return {
		success: false,
		status: latest.status,
		jobId: started.jobId,
		error: latest.hint || 'Narrative is still running. Turn on email to receive it when it finishes.',
		elapsedMs: latest.elapsedMs
	};
}

export async function analyzeSchedule(tasks, dispatch, options = {}) {
	const taskListString = (tasks || []).map(serializeTaskForAi).join('\n');
	return complete(dispatch, SCHEDULE_SYSTEM, scheduleUserPrompt(taskListString), options);
}

export async function analyzeRepository(tasks, updateNotes, dispatch, options = {}) {
	if (!updateNotes || !updateNotes.length) {
		return 'No field updates found to analyze.';
	}
	const taskListString = (tasks || []).map(serializeTaskForAi).join('\n');
	const notesContext = updateNotes.map((note, i) => `Note ${i + 1}: "${note}"`).join('\n');
	return complete(dispatch, REPOSITORY_SYSTEM, repositoryUserPrompt(notesContext, taskListString), options);
}

export async function generateNarrative(tasks, scope, targetAudience, useTemplate, templateNotes, dispatch, options = {}) {
	const taskListString = (tasks || []).map(serializeTaskForAi).join('\n');
	const notes = useTemplate ? (templateNotes || []) : [];
	return complete(
		dispatch,
		NARRATIVE_SYSTEM,
		narrativeUserPrompt({
			scope,
			targetAudience,
			templateNotes: notes,
			taskListString
		}),
		options
	);
}
